import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, BellOff, Volume2, VolumeX, AlertTriangle, Download } from "lucide-react";
import {
  isPushSupported,
  getPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isStandalone,
  isIOS,
} from "@/lib/push";
import { isChimeMuted, setChimeMuted, playChime } from "@/lib/chime";

// Turn a Cloudinary API response (or thrown error object) into a gentle
// human-friendly toast message. Never surfaces raw stack traces or JSON.
const cloudinaryFailMsg = (data) => {
  const msg = data?.error?.message || "";
  if (/File size too large/i.test(msg)) return "That photo is too large. Try one under 10 MB.";
  if (/Invalid image file/i.test(msg)) return "That file doesn't look like a photograph we can read.";
  if (/timestamp/i.test(msg)) return "The upload link expired. Please try again.";
  return "Couldn't upload the photo. Please try again.";
};

const Settings = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const openNotifications = location.hash === "#notifications";
  const [form, setForm] = useState({ display_name: "", bio: "", avatar_url: "", location: "" });
  const [links, setLinks] = useState([
    { label: "Website", url: "" },
    { label: "Website", url: "" },
  ]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cloudinaryReady, setCloudinaryReady] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false, busy: false });
  const [chimeMuted, setChimeMutedState] = useState(isChimeMuted());
  const [digestOn, setDigestOn] = useState(user?.rooms_digest_email !== false);
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    setDigestOn(user?.rooms_digest_email !== false);
  }, [user?.rooms_digest_email]);

  const toggleDigest = async () => {
    const next = !digestOn;
    setDigestOn(next);
    setDigestBusy(true);
    try {
      const { data: updated } = await api.put("/users/me/favorite-rooms", {
        favorite_rooms: user?.favorite_rooms || [],
        rooms_digest_email: next,
      });
      // The PUT already returns the fresh user — use it directly, avoiding
      // an extra /auth/me round-trip.
      setUser(updated);
      toast.success(next ? "The Wednesday digest will arrive in your inbox." : "Digest turned off.");
    } catch {
      setDigestOn(!next);
      toast.error("Couldn't save. Please try again.");
    } finally {
      setDigestBusy(false);
    }
  };
  const [dangerOpen, setDangerOpen] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/cloudinary/status").then(({ data }) => setCloudinaryReady(data.configured)).catch(() => {});
    if (user) {
      setForm({
        display_name: user.display_name || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || "",
        location: user.location || "",
      });
      const userLinks = user.links || [];
      setLinks([
        { label: "Website", url: userLinks[0]?.url || "" },
        { label: "Website", url: userLinks[1]?.url || "" },
      ]);
    }
    setPushStatus((s) => ({
      ...s,
      supported: isPushSupported(),
      subscribed: getPushPermission() === "granted",
    }));
  }, [user]);

  // If the user arrived at "/settings#notifications" (from the profile
  // dropdown or a direct link), scroll them past the profile-edit form
  // and land on the Notifications section. Otherwise the h1 change is the
  // only visible signal, and the user still sees the form — the exact
  // confusing UX we're fixing.
  useEffect(() => {
    if (!openNotifications) return;
    // Two rAFs + a small timeout is the reliable way to wait for the tall
    // form above the notifications block to finish laying out.
    let frame1, frame2, t;
    frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        t = setTimeout(() => {
          const el = document.getElementById("notifications");
          if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
        }, 80);
      });
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
      clearTimeout(t);
    };
  }, [openNotifications]);


  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleAvatarFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (!cloudinaryReady) {
      toast.error("Direct uploads require Cloudinary credentials. For now, paste a URL below.");
      return;
    }
    setUploading(true);
    try {
      const sig = await api.get("/cloudinary/signature", { params: { resource_type: "image", folder: "avatars/" } });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.data.api_key);
      fd.append("timestamp", sig.data.timestamp);
      fd.append("signature", sig.data.signature);
      fd.append("folder", sig.data.folder);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/image/upload`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      if (data.secure_url) {
        setForm((f) => ({ ...f, avatar_url: data.secure_url }));
        toast.success("Photo uploaded. Don't forget to save.");
      } else {
        toast.error(cloudinaryFailMsg(data));
      }
    } catch {
      toast.error("Couldn't upload the photo. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        // Only include non-empty links
        links: links.filter((l) => l.url.trim()).map((l) => ({ label: l.label, url: l.url.trim() })),
      };
      const { data } = await api.put("/users/me", payload);
      setUser(data);
      toast.success("Profile updated.");
      navigate(`/profile/${data.username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const togglePush = async () => {
    setPushStatus((s) => ({ ...s, busy: true }));
    try {
      if (pushStatus.subscribed) {
        await unsubscribeFromPush();
        toast.success("Notifications turned off.");
        setPushStatus((s) => ({ ...s, subscribed: false, busy: false }));
      } else {
        await subscribeToPush();
        toast.success("You'll be gently notified of moments in the salon.");
        setPushStatus((s) => ({ ...s, subscribed: true, busy: false }));
      }
    } catch (err) {
      const msg = err?.message || "Could not update notifications";
      toast.error(msg);
      setPushStatus((s) => ({ ...s, busy: false }));
    }
  };

  const showIosHint = isIOS() && !isStandalone();

  return (
    <div data-testid="settings-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />
      <div className="container-belle" style={{ paddingTop: 110, paddingBottom: 80, maxWidth: 720 }}>
        {openNotifications ? (
          <>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Settings &amp; Notifications</div>
            <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 12, marginBottom: 40 }}>
              Your salon preferences.
            </h1>
          </>
        ) : (
          <>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Réglages</div>
            <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 12, marginBottom: 40 }}>
              Edit your profile.
            </h1>
          </>
        )}

        <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <div>
            <label className="overline">Display name</label>
            <input data-testid="settings-name" className="input-belle" value={form.display_name} onChange={set("display_name")} />
          </div>
          <div>
            <label className="overline">Location</label>
            <input data-testid="settings-location" className="input-belle" value={form.location} onChange={set("location")} placeholder="Paris, 6ème" />
          </div>
          <div>
            <label className="overline">Your links (up to two)</label>
            <p className="editorial-body" style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginTop: 4, marginBottom: 10, lineHeight: 1.5 }}>
              Where can members find more of your work? These appear on your profile&apos;s About tab.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <div className="overline" style={{ fontSize: 10 }}>Website 1</div>
              <input
                data-testid="settings-link-1-url"
                className="input-belle"
                type="url"
                value={links[0].url}
                onChange={(e) => setLinks([{ ...links[0], url: e.target.value }, links[1]])}
                placeholder="belleinspiration.com"
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, alignItems: "center" }}>
              <div className="overline" style={{ fontSize: 10 }}>Website 2</div>
              <input
                data-testid="settings-link-2-url"
                className="input-belle"
                type="url"
                value={links[1].url}
                onChange={(e) => setLinks([links[0], { ...links[1], url: e.target.value }])}
                placeholder="https://…"
              />
            </div>
            <p style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic", marginTop: 8 }}>
              A calm space to point members to your work. Add up to two websites you&apos;d like members to see.
            </p>
          </div>
          <div>
            <label className="overline">Profile photo</label>
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 12, flexWrap: "wrap" }}>
              {form.avatar_url ? (
                <img
                  src={form.avatar_url}
                  alt=""
                  data-testid="avatar-preview"
                  style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--border-default)" }}
                />
              ) : (
                <div
                  data-testid="avatar-placeholder"
                  style={{ width: 96, height: 96, borderRadius: "50%", border: "1px dashed var(--border-default)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 14, background: "var(--bg-alt)" }}
                >
                  no photo
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  className="btn-secondary"
                  data-testid="upload-avatar-button"
                  style={{ cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.5 : 1, display: "inline-block" }}
                >
                  {uploading ? "Uploading…" : (form.avatar_url ? "Change photo" : "Upload photo")}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleAvatarFile(e.target.files?.[0])}
                    disabled={!cloudinaryReady || uploading}
                    data-testid="upload-avatar-input"
                  />
                </label>
                <p className="editorial-body" style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", margin: 0 }}>
                  JPG or PNG. You can change this any time.
                </p>
              </div>
            </div>
            <div style={{ marginTop: 18 }}>
              <label className="overline" style={{ fontSize: 10, opacity: 0.7 }}>Or paste a URL</label>
              <input data-testid="settings-avatar" className="input-belle" value={form.avatar_url} onChange={set("avatar_url")} placeholder="https://…" />
            </div>
          </div>
          <div>
            <label className="overline">Bio</label>
            <textarea
              data-testid="settings-bio"
              className="input-belle"
              rows={4}
              style={{ minHeight: 100, resize: "vertical", borderBottom: "1px solid var(--border-dark)", fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontStyle: "italic" }}
              value={form.bio}
              onChange={set("bio")}
              placeholder="Tell the salon a little about yourself…"
            />
          </div>

          <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="settings-save">
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          </div>
        </form>

        {/* Notifications */}
        <div
          id="notifications"
          style={{
            marginTop: 56,
            padding: 32,
            border: "1px solid var(--border-default)",
            background: "var(--bg-alt)",
            scrollMarginTop: 96,
          }}
        >
          <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8 }}>
            <Bell size={12} strokeWidth={1.5} /> Notifications
          </div>
          <h2 className="editorial-h2" style={{ fontSize: 26, marginTop: 10, marginBottom: 12, color: "var(--text-primary)" }}>
            Quiet whispers from the salon.
          </h2>
          <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 20 }}>
            A gentle notification when there is something worth your attention — a new editor's letter, a live broadcast, a moment to come home to. Never noise. Never every post.
          </p>

          {!pushStatus.supported ? (
            <p className="overline" style={{ color: "var(--text-muted)", opacity: 0.7 }}>
              Notifications aren't supported on this browser.
            </p>
          ) : showIosHint ? (
            <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic", lineHeight: 1.6 }}>
              On iPhone, notifications only work once Le Salon is added to your home screen. Look for the "Add to home screen" invitation, then return here to enable.
            </p>
          ) : (
            <button
              onClick={togglePush}
              disabled={pushStatus.busy}
              data-testid="toggle-notifications-button"
              className={pushStatus.subscribed ? "btn-secondary" : "btn-accent"}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              {pushStatus.subscribed ? (
                <>
                  <BellOff size={14} strokeWidth={1.5} /> Turn off notifications
                </>
              ) : (
                <>
                  <Bell size={14} strokeWidth={1.5} /> {pushStatus.busy ? "Enabling…" : "Enable notifications"}
                </>
              )}
            </button>
          )}

          {/* Wednesday Rooms digest email */}
          <div style={{ marginTop: 28, paddingTop: 24, borderTop: "1px solid var(--border-default)" }}>
            <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Bell size={12} strokeWidth={1.5} /> Wednesday Rooms digest
            </div>
            <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 14 }}>
              A quiet Wednesday letter with the week&apos;s highlights from your favorite rooms. Nothing else — just the rooms you&apos;ve hearted.
            </p>
            <button
              type="button"
              onClick={toggleDigest}
              disabled={digestBusy}
              data-testid="toggle-digest-button"
              className={digestOn ? "btn-secondary" : "btn-accent"}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              {digestOn ? "Turn Wednesday digest off" : "Send me the Wednesday digest"}
            </button>
          </div>

          {/* Salon chime — in-app sound for new letters & mentions */}
          <div style={{ marginTop: 28, paddingTop: 24, borderTop: "1px solid var(--border-default)" }}>
            <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              {chimeMuted ? <VolumeX size={12} strokeWidth={1.5} /> : <Volume2 size={12} strokeWidth={1.5} />}
              Salon chime
            </div>
            <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 14 }}>
              A soft two-note bell when a new letter or @mention arrives — only if you're elsewhere on the site, never while you&apos;re already reading.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => {
                  const next = !chimeMuted;
                  setChimeMuted(next);
                  setChimeMutedState(next);
                  if (!next) {
                    // Preview the chime when re-enabling so they know what it sounds like.
                    setTimeout(() => playChime(), 100);
                  }
                }}
                data-testid="toggle-chime-button"
                className={chimeMuted ? "btn-accent" : "btn-secondary"}
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                {chimeMuted ? (
                  <><Volume2 size={14} strokeWidth={1.5} /> Turn chime on</>
                ) : (
                  <><VolumeX size={14} strokeWidth={1.5} /> Mute the chime</>
                )}
              </button>
              {!chimeMuted && (
                <button
                  type="button"
                  onClick={() => playChime()}
                  data-testid="preview-chime-button"
                  className="btn-secondary"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  Preview
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Your data — Download */}
        <div
          data-testid="data-export"
          style={{
            marginTop: 56,
            padding: 32,
            border: "1px solid var(--border-default)",
            background: "var(--bg-alt)",
          }}
        >
          <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8 }}>
            <Download size={12} strokeWidth={1.5} /> Your data, your archive
          </div>
          <h2 className="editorial-h2" style={{ fontSize: 26, marginTop: 10, marginBottom: 12, color: "var(--text-primary)" }}>
            Download my data.
          </h2>
          <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 20 }}>
            A single zip file with your profile, every post, every letter, every comment, every note you&apos;ve sent, and links to every photograph you&apos;ve shared. Yours to keep, forever.
          </p>
          <button
            type="button"
            data-testid="download-my-data-button"
            onClick={async () => {
              try {
                const res = await api.get("/users/me/export", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/zip" });
                const url = URL.createObjectURL(blob);
                const cd = res.headers?.["content-disposition"] || "";
                const match = cd.match(/filename="?([^"]+)"?/);
                const filename = match ? match[1] : "le-salon-export.zip";
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success("Your archive has been downloaded.");
              } catch (err) {
                toast.error("Could not prepare your archive. Please try again.");
              }
            }}
            className="btn-secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Download size={14} strokeWidth={1.6} /> Download my data
          </button>
        </div>

        {/* Danger zone — Delete account */}
        <div
          data-testid="danger-zone"
          style={{
            marginTop: 56,
            padding: 32,
            border: "1px solid var(--brand-accent)",
            background: "var(--surface)",
          }}
        >
          <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={12} strokeWidth={1.5} /> Leaving the salon
          </div>
          <h2 className="editorial-h2" style={{ fontSize: 26, marginTop: 10, marginBottom: 12, color: "var(--text-primary)" }}>
            Close your account.
          </h2>
          <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 20 }}>
            This will permanently remove your profile, posts, comments, letters, and follows. It cannot be undone. If you simply want a break, you can sign out instead.
          </p>
          {!dangerOpen ? (
            <button
              type="button"
              onClick={() => setDangerOpen(true)}
              data-testid="open-delete-account"
              className="btn-secondary"
              style={{ borderColor: "var(--brand-accent)", color: "var(--brand-accent)" }}
            >
              Delete my account…
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="overline">Your current password</label>
                <input
                  data-testid="delete-account-password"
                  className="input-belle"
                  type="password"
                  value={deletePw}
                  onChange={(e) => setDeletePw(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label className="overline">Type <span style={{ color: "var(--brand-accent)", fontWeight: 600 }}>delete</span> to confirm</label>
                <input
                  data-testid="delete-account-confirm"
                  className="input-belle"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="delete"
                />
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                <button
                  type="button"
                  disabled={deleting || !deletePw || deleteConfirm.trim().toLowerCase() !== "delete"}
                  data-testid="delete-account-submit"
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await api.delete("/users/me", {
                        data: { password: deletePw, confirmation: deleteConfirm.trim().toLowerCase() },
                      });
                      toast.success("Your account has been closed. Farewell.");
                      logout();
                      navigate("/", { replace: true });
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Could not close account");
                      setDeleting(false);
                    }
                  }}
                  className="btn-primary"
                  style={{ background: "var(--brand-accent)", borderColor: "var(--brand-accent)" }}
                >
                  {deleting ? "Closing…" : "Permanently delete"}
                </button>
                <button
                  type="button"
                  onClick={() => { setDangerOpen(false); setDeletePw(""); setDeleteConfirm(""); }}
                  data-testid="delete-account-cancel"
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Settings;
